/**
 * CompanySwitcher Component
 * Dropdown for employees to switch between companies in preview mode
 * Modeled after StoreSwitcher pattern
 */

import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';

const CompanySwitcher = ({ variant = 'desktop' }) => {
  const navigate = useNavigate();
  const {
    user,
    company: currentCompany,
    switchCompany,
    canProceedToShopping,
    hasMultipleCompanies
  } = useAuth();

  const [isOpen, setIsOpen] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);
  const [switchingCompanyId, setSwitchingCompanyId] = useState(null);
  const [showSuccessOverlay, setShowSuccessOverlay] = useState(false);
  const [switchedCompanyName, setSwitchedCompanyName] = useState('');
  const dropdownRef = useRef(null);

  const companies = user?.companies || [];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Auto-redirect after successful company switch
  useEffect(() => {
    if (showSuccessOverlay) {
      const timer = setTimeout(() => {
        setShowSuccessOverlay(false);
        navigate('/', { replace: true });
      }, 2500);

      return () => clearTimeout(timer);
    }
  }, [showSuccessOverlay, navigate]);

  // Don't render if not an employee or has only one company
  if (!canProceedToShopping() || !hasMultipleCompanies()) {
    return null;
  }

  const handleCompanySwitch = async (company) => {
    if (company.companyId === currentCompany?.companyId || isSwitching) return;

    setIsSwitching(true);
    setSwitchingCompanyId(company.companyId);
    setSwitchedCompanyName(company.displayName || company.companyName);

    try {
      // Map to the shape authService.switchCompany expects (id, not companyId)
      await switchCompany({
        id: company.companyId,
        name: company.companyName,
        slug: company.slug,
        displayName: company.displayName,
        isMed: company.isMed || false,
        isRec: company.isRec || false,
        allowCustomCaseUnitLabels: company.allowCustomCaseUnitLabels || false,
      });
      setIsOpen(false);
      setIsSwitching(false);
      setSwitchingCompanyId(null);

      // Show success overlay
      setShowSuccessOverlay(true);
    } catch (error) {
      console.error('Error switching companies:', error);
      alert('Failed to switch companies. Please try again.');
      setIsSwitching(false);
      setSwitchingCompanyId(null);
    }
  };

  const toggleDropdown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isSwitching && !showSuccessOverlay) {
      setIsOpen(!isOpen);
    }
  };

  const currentDisplayName = currentCompany?.displayName || currentCompany?.companyName || 'Select Company';

  return (
    <>
      <div className="dropdown" ref={dropdownRef}>
        <button
          className={`btn ${variant === 'mobile' ? 'btn-dark btn-sm' : 'btn-outline-light'} dropdown-toggle d-flex align-items-center rounded-0 ${
            (isSwitching || showSuccessOverlay) ? 'disabled' : ''
          }`}
          type="button"
          onClick={toggleDropdown}
          disabled={isSwitching || showSuccessOverlay}
          style={{ minWidth: variant === 'mobile' ? '140px' : '200px' }}
        >
          <i className="bi bi-building me-2"></i>
          <span className="flex-grow-1 text-start text-truncate">
            {isSwitching ? 'Switching...' : currentDisplayName}
          </span>
          {isSwitching && (
            <div className="spinner-border spinner-border-sm ms-2" role="status">
              <span className="visually-hidden">Switching...</span>
            </div>
          )}
        </button>

        <div className={`dropdown-menu dropdown-menu-end shadow-lg ${isOpen ? 'show' : ''}`} style={{ minWidth: '320px' }}>
          <div className="dropdown-header d-flex align-items-center justify-content-between">
            <span className="fw-bold">Switch Company</span>
            <span className="badge bg-info">{companies.length} available</span>
          </div>

          <div className="dropdown-divider"></div>

          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {companies.map((company) => {
              const isCurrentCompany = company.companyId === currentCompany?.companyId;
              const isLoadingThis = switchingCompanyId === company.companyId;

              return (
                <button
                  key={company.companyId}
                  type="button"
                  className={`dropdown-item px-3 py-2 ${isCurrentCompany ? 'active' : ''} ${isLoadingThis ? 'disabled' : ''}`}
                  onClick={() => !isCurrentCompany && !isLoadingThis && handleCompanySwitch(company)}
                  disabled={isCurrentCompany || isLoadingThis}
                  style={{
                    cursor: isCurrentCompany ? 'default' : (isLoadingThis ? 'wait' : 'pointer'),
                    opacity: isLoadingThis ? 0.7 : 1
                  }}
                >
                  <div className="d-flex align-items-start">
                    <div className="me-3 mt-1">
                      {isCurrentCompany ? (
                        <i className="bi bi-check-circle-fill text-success"></i>
                      ) : isLoadingThis ? (
                        <div className="spinner-border spinner-border-sm text-primary" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      ) : (
                        <i className="bi bi-building text-muted"></i>
                      )}
                    </div>

                    <div className="flex-grow-1">
                      <div className="fw-semibold text-dark">
                        {company.displayName || company.companyName}
                        {isCurrentCompany && (
                          <span className="badge bg-success ms-2 small">Current</span>
                        )}
                      </div>

                      {company.dbaName && company.dbaName !== company.companyName && (
                        <div className="small text-muted mt-1">
                          <i className="bi bi-briefcase me-1" style={{ fontSize: '0.75rem' }}></i>
                          DBA: {company.dbaName}
                        </div>
                      )}

                      {isLoadingThis && (
                        <div className="small text-primary mt-1">
                          Switching company...
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Success Overlay */}
      {showSuccessOverlay && createPortal(
        <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
             style={{
               backgroundColor: 'rgba(0, 0, 0, 0.8)',
               zIndex: 9999,
               backdropFilter: 'blur(4px)'
             }}>
          <div className="card border-0 shadow-lg" style={{ maxWidth: '420px' }}>
            <div className="card-body text-center p-5">
              <div className="mb-4">
                <div className="rounded-circle bg-info bg-opacity-10 d-inline-flex align-items-center justify-content-center mx-auto"
                     style={{ width: '80px', height: '80px' }}>
                  <i className="bi bi-building text-info" style={{ fontSize: '2.5rem' }}></i>
                </div>
              </div>

              <h3 className="text-info fw-bold mb-3">Company Switched!</h3>
              <p className="text-muted mb-2">
                Now previewing <strong>{switchedCompanyName}</strong>
              </p>
              <p className="small text-muted mb-4">
                Products will update to reflect this company's catalog.
              </p>

              <div className="d-flex align-items-center justify-content-center text-muted">
                <div className="spinner-border spinner-border-sm me-2" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <small>Refreshing products...</small>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default CompanySwitcher;
